import { useNavigate } from '@tanstack/react-router';
import { useEffect, type FormEvent } from 'react';
import { colourPresentation } from '../../appdata.js';
import { CatalogueNotice } from '../components/CatalogueNotice.js';
import {
  DesktopDoorPreview,
  MobileDoorPreview,
  type DoorPreviewPrice,
} from '../components/DoorPreview.js';
import { StepNavigation, StepProgress } from '../components/StepProgress.js';
import { colourPricesForSize } from '../lib/catalogue.js';
import { evaluateLock } from '../lib/lock.js';
import { evaluateMeasurements } from '../lib/measurements.js';
import { useMeasurementsReadiness } from '../lib/useMeasurements.js';
import { useDoorStore } from '../stores/doorStore.js';

export function ColourStep() {
  const { draft, evaluation, catalogue, products, retry } = useMeasurementsReadiness();
  const selectFrameColour = useDoorStore((state) => state.selectFrameColour);
  const navigate = useNavigate();

  useEffect(() => {
    if (!draft.doorType) {
      return;
    }
    for (const colour of colourPresentation.colours) {
      const image = new Image();
      image.decoding = 'async';
      image.src = colour.photos[draft.doorType].src;
    }
  }, [draft.doorType]);

  if (!draft.doorType || !evaluation.size) {
    return (
      <>
        <StepProgress currentIndex={3} />
        <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10">
          <p role="status">Checking your measurements and catalogue availability...</p>
        </main>
      </>
    );
  }

  const doorType = draft.doorType;
  const size = evaluation.size;
  const selected = colourPresentation.colours.find((colour) => colour.name === draft.frameColour);
  if (!selected) {
    throw new Error(`Missing presentation for frame colour ${draft.frameColour}`);
  }
  const prices = products ? colourPricesForSize(products, doorType, size) : undefined;
  const selectedPrice = prices?.get(draft.frameColour);
  const lock = evaluateLock(draft.lock, doorType, evaluation);
  const canContinue = evaluation.canContinue && lock.canContinue && selectedPrice !== undefined;
  const price: DoorPreviewPrice =
    catalogue.status === 'ready'
      ? selectedPrice === undefined
        ? { status: 'unavailable' }
        : { status: 'estimate', price: selectedPrice }
      : { status: catalogue.status === 'loading' ? 'checking' : 'unverified' };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const current = useDoorStore.getState().draft;
    const measurements = evaluateMeasurements(current, catalogue);
    const currentPrices =
      products && current.doorType && measurements.size
        ? colourPricesForSize(products, current.doorType, measurements.size)
        : undefined;
    if (
      evaluateLock(current.lock, current.doorType, measurements).canContinue &&
      currentPrices?.has(current.frameColour)
    ) {
      void navigate({ to: '/planner/review' });
    }
  };

  return (
    <>
      <StepProgress currentIndex={3} />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 pb-28 sm:py-10 lg:pb-10">
        <h1 className="mb-2 text-2xl font-semibold text-gray-800 sm:text-3xl">
          {colourPresentation.heading}
        </h1>
        <p className="mb-8 text-sm text-gray-500">
          {colourPresentation.introduction} {colourPresentation.hardwareNote[doorType]}
        </p>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="mb-5 flex items-center gap-3 border-b border-gray-100 pb-5">
                  <span
                    aria-hidden="true"
                    className="size-10 shrink-0 rounded-lg border-2 border-white shadow-md"
                    style={{ backgroundColor: selected.hex }}
                  />
                  <div>
                    <p className="text-xs tracking-wide text-gray-500 uppercase">
                      {colourPresentation.selectedLabel}
                    </p>
                    <p className="text-sm font-semibold text-gray-800">{selected.name}</p>
                  </div>
                </div>

                <fieldset
                  role="radiogroup"
                  data-name="frame-colour-grid"
                  aria-label="Frame colour"
                  className="grid grid-cols-5 gap-3 sm:gap-4"
                >
                  <legend className="sr-only">Frame colour</legend>
                  {colourPresentation.colours.map((colour) => {
                    const checked = colour.name === draft.frameColour;
                    const unavailable = prices !== undefined && !prices.has(colour.name);
                    const unavailableId = `frame-colour-${colour.id}-unavailable`;
                    return (
                      <label
                        key={colour.id}
                        className={`group flex min-w-0 flex-col items-center rounded-xl [touch-action:manipulation] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand ${
                          unavailable ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                        }`}
                      >
                        <input
                          type="radio"
                          name="frame-colour"
                          data-name={`colour-swatch-${colour.id}`}
                          aria-label={colour.name}
                          aria-describedby={unavailable ? unavailableId : undefined}
                          checked={checked}
                          disabled={unavailable}
                          onChange={() => selectFrameColour(colour.name)}
                          className="sr-only"
                        />
                        <span className="relative w-full">
                          <span
                            className={`relative block aspect-square w-full rounded-xl border-2 transition-all duration-150 ${
                              checked
                                ? 'scale-105 border-progress shadow-lg'
                                : unavailable
                                  ? 'border-gray-200'
                                  : 'border-gray-200 group-hover:border-gray-400 group-hover:scale-105'
                            }`}
                            style={{ backgroundColor: colour.hex }}
                          >
                            {checked && !unavailable && (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <span className="flex size-6 items-center justify-center rounded-full bg-white/90 shadow-sm">
                                  <svg
                                    aria-hidden="true"
                                    className="size-3 text-progress"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path d="m5 12 4 4L19 6" />
                                  </svg>
                                </span>
                              </span>
                            )}
                          </span>
                          {unavailable && (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <span className="flex size-6 items-center justify-center rounded-full bg-white/90 shadow-sm">
                                <svg
                                  aria-hidden="true"
                                  className="size-6 text-progress"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                >
                                  <path d="m6 6 12 12M18 6 6 18" />
                                </svg>
                              </span>
                            </span>
                          )}
                        </span>
                        <span
                          className={`mt-1.5 min-h-4 w-[calc(100%+0.75rem)] text-center text-xs leading-tight break-words transition-colors sm:w-[calc(100%+1rem)] ${
                            checked
                              ? 'font-semibold text-progress'
                              : unavailable
                                ? 'text-gray-500'
                                : 'text-gray-500 group-hover:text-gray-700'
                          }`}
                        >
                          {colour.name}
                        </span>
                        {unavailable && (
                          <span
                            id={unavailableId}
                            className="w-[calc(100%+0.75rem)] text-center text-[10px] break-words text-gray-600 sm:w-[calc(100%+1rem)]"
                          >
                            {colourPresentation.unavailableLabel}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </fieldset>
                {prices && selectedPrice === undefined && (
                  <p role="alert" className="mt-4 text-xs text-red-600">
                    This frame colour is not available in this size. Choose another colour to
                    continue.
                  </p>
                )}
                <CatalogueNotice status={catalogue.status} retry={retry} step="colour" />
                <p className="mt-5 border-t border-gray-100 pt-4 text-xs leading-relaxed text-gray-400">
                  {colourPresentation.indicativeNote}
                </p>
              </div>
              <MobileDoorPreview
                step="colour"
                doorType={doorType}
                frameColour={draft.frameColour}
                size={size}
                lock={draft.lock}
                evaluation={lock}
                price={price}
                depth={draft.measurements.depth}
              />
              <StepNavigation currentIndex={3} canContinue={canContinue} submit mobileSticky />
            </div>
            <DesktopDoorPreview
              step="colour"
              doorType={doorType}
              frameColour={draft.frameColour}
              size={size}
              lock={draft.lock}
              evaluation={lock}
              price={price}
            />
          </div>
        </form>
      </main>
    </>
  );
}
